import {TimeBasedForwarding} from "../app/types";
import {FormSection} from "../common/components/SectionedForm";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {selectedSiteSelector} from "../authentication/redux";
import React, {ReactElement, ReactNode, useCallback} from "react";
import {api} from "../common/api";
import {useFetchData} from "../common/servercall/useFetchData";
import {LabelValues, LabelValueType} from "../common/components/LabelValues";
import PaperStack from "../common/form/PaperStack";
import FormGroup from "../common/form/FormGroup";
import {Schedule} from "@mui/icons-material";
import {useFormSubmissionButtons} from "../common/form/useFormSubmissmionButtons";
import {Box, Grid, Link, Paper, Table, TableContainer} from "@mui/material";
import {formatNumberToForwardTo} from "../common/util/formatters";
import {usePhoneNumbersToForwardTo} from "../common/servercall/usePhoneNumbersToForwardTo";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import {TimeBasedForwardingForm} from "./TimeBasedForwardingForm";
import {detailUrlOpeningHours} from "../schedules/openinghours";

export const useTimeBasedSchedulesSection = (userId: string | null, agId: string | null, ivrId: string | null, allowMultipleForwards: boolean ) : FormSection<TimeBasedForwarding> => {
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)
    const {buildFormButtons} = useFormSubmissionButtons(false)
    const {phoneNumbersToForwardTo} = usePhoneNumbersToForwardTo(site)

    const formatNumber = useCallback((extension?: string | null) => formatNumberToForwardTo(t, extension || null, phoneNumbersToForwardTo), [phoneNumbersToForwardTo, t])

    const fetchTimeBasedForwarding = useCallback(
        async (userId: string | null, agId: string | null, ivrId: string | null ): Promise<TimeBasedForwarding | undefined> => {
            if (!site) return
            const { data } = await api.get<TimeBasedForwarding>(`/api/site/${site.productId}/time-based-forwarding/${!!userId? 'user' : !!agId? 'ag' : 'ivr'}/${userId || agId || ivrId}`)
            return data;
        },
        [site]
    )
    const { data, isFetching, error, refetch } = useFetchData<TimeBasedForwarding>(useCallback(() => fetchTimeBasedForwarding(userId, agId, ivrId), [userId, agId, ivrId, fetchTimeBasedForwarding]))

    const onSubmit = useCallback((onSucceeded: () => void) =>
            async (formData: TimeBasedForwarding) => {
                if (!site) return
                await api.put(`/api/site/${site.productId}/time-based-forwarding/${!!userId ? 'user' : !!agId ? 'ag' : 'ivr'}/${userId || agId || ivrId}`, {
                    ...formData,
                    forwardingSchedules: formData
                        .forwardingSchedules?.map(
                            it => (
                                {
                                    scheduleUuid: it.scheduleUuid,
                                    forwardingNumber: it.forwardingNumber,
                                    scheduleName: it.scheduleUuid === null ? null : it.scheduleName
                                })
                        )
                })
                await refetch()
                onSucceeded();
            },
        [site, userId, agId, ivrId, refetch],
    )


    const nightServiceModeLabelValue = useCallback((value: TimeBasedForwarding): LabelValueType[] =>
        [
            {
                label: t('flows.edit.night-service'),
                value: <>{t(`flows.edit.night-service-modes.${value?.nightServiceMode}`)}</>,
                type: 'FULL_LINE'
            }
        ], [t])

    const selectiveForwardSchedules = useCallback((value: TimeBasedForwarding): LabelValueType[] => {
        return [
            {
                label: t('selective-call-forward.holiday-schedule'),
                value: value.forwardingSchedules && value.forwardingSchedules.length > 0?
                        <Grid container item xs={12} lg={6} >
                            <TableContainer component={Paper} sx={{mt: 1}}>
                                <Table sx={{minWidth: 300}} size={'small'}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{fontWeight: 700}}>{t('schedules.schedule')}</TableCell>
                                            <TableCell style={{fontWeight: 700}} align="right">
                                                {t('time-based-forwarding.forwarding-number')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {value.forwardingSchedules &&
                                            value.forwardingSchedules.map(it => (
                                                <TableRow key={it.scheduleUuid}
                                                          sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                                    <TableCell scope="row">
                                                        <>{it.scheduleName ?
                                                            <Link
                                                                href={detailUrlOpeningHours.replace(':id', it.scheduleUuid)}>{it.scheduleName}</Link>
                                                            : t('general.none')}</>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Box alignSelf={'center'} sx={{marginLeft: 1}}>
                                                            {formatNumber(it.forwardingNumber)}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid> : <>{t('general.none')}</>,
                type: 'FULL_LINE'
            }
        ];
    }, [t, formatNumber])

    const selectiveForwardOpeningHours = useCallback(
        (value: TimeBasedForwarding): LabelValueType[] => [
            {
                label: t('time-based-forwarding.openinghours-schedule'),
                value: <>{value?.openingHoursSchedule?.scheduleName?
                    <Link href={detailUrlOpeningHours.replace(':id', value?.openingHoursSchedule?.scheduleUuid)}>{value?.openingHoursSchedule?.scheduleName}</Link>
                    : t('general.none')}</>,
            },
            ...(value?.openingHoursSchedule? [{
                label: t('time-based-forwarding.openinghours-number'),
                value: <>{formatNumber(value?.openingHoursSchedule?.forwardingNumber)}</>,
            }] : [])
            ,
        ],
        [t, formatNumber]
    )

    const renderTimeBasedForwarding = useCallback(
        (value: TimeBasedForwarding): ReactNode => {
            return (
                <PaperStack>
                    <FormGroup fullWidth={true}>
                        {!userId ? <LabelValues labelValues={nightServiceModeLabelValue(value)} /> : <></>}
                        <LabelValues labelValues={selectiveForwardSchedules(value)}  />
                        <Box flexWrap={"wrap"} width="100%"></Box>
                        <LabelValues labelValues={selectiveForwardOpeningHours(value)} />
                    </FormGroup>
                    <></>
                </PaperStack>
            )
        },
        [userId, nightServiceModeLabelValue, selectiveForwardSchedules, selectiveForwardOpeningHours]
    )

    const renderForm = useCallback((timeBasedForwarding: TimeBasedForwarding, cancelEditButton: ReactElement, onSucceeded: () => void): ReactNode =>
            <TimeBasedForwardingForm fullWidth={true}
                                     allowMultipleForwards={allowMultipleForwards}
                                     phoneNumbersToForwardTo={phoneNumbersToForwardTo}
                                     timeBasedForwarding={timeBasedForwarding}
                                     buildFormButtons={buildFormButtons(cancelEditButton)}
                                     onSubmit={onSubmit(onSucceeded)} />,
        [onSubmit, buildFormButtons, phoneNumbersToForwardTo, allowMultipleForwards])

    return {
        title: t('time-based-forwarding.title'),
        data: data,
        isFetching: isFetching,
        error: error,
        refetch: refetch,
        renderDetails: renderTimeBasedForwarding,
        renderForm: renderForm,
        enabled: () => true,
        icon: () => <Schedule />
    }
}